import { UseMutateFunction } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  HostingSiteAddV2Body,
  HostingSiteAddV2200,
} from '@newfold/huapi-js/src/index.schemas';

import useAccount from '~/hooks/useAccount';
import useHostingInfo from '~/hooks/useHostingInfo';
import useHostingNavigate from '~/hooks/useHostingNavigate';
import BtnGroup from '~/scenes/AddSetupSiteFlows/components/MultiStepCard/BtnGroup';
import CardHeader from '~/scenes/AddSetupSiteFlows/components/MultiStepCard/CardHeader';
import DataCenter from '~/scenes/Cloud/components/DataCenter';
import Storage from '~/scenes/Cloud/components/Storage';
import { validateStorage } from '~/utils/cloud/validations';
import { getSiteStorageQuotaLimit } from '~/utils/hosting/helpers';

import { defaultFormData, FormDataType } from '../..';

export type ChooseResourcesPropOptions = {
  formData: FormDataType;
  createWebsite?: UseMutateFunction<
    AxiosResponse<HostingSiteAddV2200, any>,
    any,
    { hostingId: string; data: HostingSiteAddV2Body },
    void
  >;
  changeLoading?: boolean;
};

const ChooseResources = ({
  formData,
  createWebsite = () => {},
  changeLoading = false,
}: ChooseResourcesPropOptions) => {
  const { id: hostingId } = useAccount();
  const { t } = useTranslation(['addSites']);
  const { t: tCloud } = useTranslation('cloud');
  const navigate = useNavigate();
  const hostingNavigate = useHostingNavigate();
  const { watch, reset } = useFormContext();

  const datacenter = watch('datacenter');

  const { hostingAccount, isPending } = useHostingInfo();
  const storageInfo = hostingAccount?.data?.resources?.storage;
  const siteStorageQuota = getSiteStorageQuotaLimit(storageInfo);

  const { isValid: storageIsValid } = validateStorage({
    value: watch('disk_limit_gib'),
    min: 1,
    max: Number(storageInfo?.available),
    siteStorageQuota,
    t: tCloud,
  });

  const disabledContinueBtn = isPending || !datacenter || !storageIsValid;

  const handleCancel = () => {
    reset(defaultFormData);
    hostingNavigate();
  };

  const addSite = async () => {
    const postData = {
      subtype: 'wordpress',
      workers: 2,
      ...(datacenter && { datacenter }),
      ...(formData.disk_limit_gib && {
        disk_limit_gib: Number(formData?.disk_limit_gib),
        ss_wp_mig: true,
      }),
    };

    await createWebsite(
      { hostingId, data: postData },
      {
        onSuccess: () => navigate('../transfer'),
      },
    );
  };

  return (
    <Stack spacing={2} data-testid="choose-resources">
      <CardHeader
        headerTitle={t('chooseResources.title')}
        hasBackArrow={false}
      />
      <Typography>{t('chooseResources.description')}</Typography>
      <Grid container flexDirection="column" rowSpacing={2}>
        <Grid item xs={12} md={10} data-testid="storage-value">
          <Stack spacing={1}>
            <Typography variant="h3">{t('siteTitle.storageLabel')}</Typography>
            <Storage defaultValue={formData.disk_limit_gib} />
            <Typography variant="h4">
              {t('siteTitle.storageAvailable', {
                amount: storageInfo?.available,
                unit: storageInfo?.unit,
              })}
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} md={10} data-testid="data-center-value">
          <Stack spacing={1}>
            <Typography variant="h3">
              {t('siteTitle.dataCenterLocationLabel')}
            </Typography>
            <DataCenter showLabel={false} defaultValue={formData.datacenter} />
          </Stack>
        </Grid>
        <Grid item>
          <Stack direction="row" spacing={3} justifyContent="flex-end">
            <BtnGroup
              disabled={disabledContinueBtn}
              hasSkipBtn={false}
              hasCancelBtn
              onCancelHandler={handleCancel}
              onClickHandler={addSite}
              dataTestId={'add-site-flow-btn'}
              loading={changeLoading}
              showInstallationModal={false}
            />
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default ChooseResources;
